import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Download, RefreshCw, Link2, Unlink, CalendarCheck, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CalendarExportToolbar = ({ sessionToken }) => {
  const [connected, setConnected] = useState(false);
  const [connectedAt, setConnectedAt] = useState(null);
  const [busy, setBusy] = useState(null); // 'ics' | 'connect' | 'sync' | 'disconnect'
  const [message, setMessage] = useState(null);

  const authHeaders = { Authorization: `Bearer ${sessionToken}` };

  const fetchStatus = async () => {
    try {
      const res = await axios.get(`${API}/provider/calendar/status`, { headers: authHeaders });
      setConnected(!!res.data?.connected);
      setConnectedAt(res.data?.connected_at || null);
    } catch (e) {
      // silent — most likely auth not ready
    }
  };

  useEffect(() => {
    if (sessionToken) fetchStatus();
    // pick up the redirect ?calendar_sync=connected once
    const params = new URLSearchParams(window.location.search);
    const cal = params.get('calendar_sync');
    if (cal === 'connected') {
      setMessage({ type: 'success', text: 'Google Calendar connected.' });
      // strip query
      params.delete('calendar_sync');
      const newQs = params.toString();
      window.history.replaceState({}, '', `${window.location.pathname}${newQs ? `?${newQs}` : ''}`);
      setTimeout(fetchStatus, 500);
    } else if (cal === 'error') {
      setMessage({ type: 'error', text: `Google Calendar connection failed: ${params.get('reason') || 'unknown'}.` });
    }
    // eslint-disable-next-line
  }, [sessionToken]);

  const handleDownloadIcs = async () => {
    setBusy('ics');
    setMessage(null);
    try {
      const res = await axios.get(`${API}/provider/appointments/export.ics`, {
        headers: authHeaders,
        responseType: 'blob',
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'text/calendar' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'enayahub-appointments.ics';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      setMessage({ type: 'success', text: 'Calendar file downloaded. Open it in Apple Calendar, Outlook, or Google Calendar to import.' });
    } catch (e) {
      setMessage({ type: 'error', text: e?.response?.data?.detail || 'Failed to download .ics' });
    } finally {
      setBusy(null);
    }
  };

  const handleConnectGoogle = async () => {
    setBusy('connect');
    setMessage(null);
    try {
      const res = await axios.get(`${API}/oauth/calendar/login`, { headers: authHeaders });
      const url = res.data?.authorization_url;
      if (url) window.location.href = url;
      else setMessage({ type: 'error', text: 'Could not start Google authorization.' });
    } catch (e) {
      setMessage({ type: 'error', text: e?.response?.data?.detail || 'Failed to start Google sign-in' });
    } finally {
      setBusy(null);
    }
  };

  const handleSync = async () => {
    setBusy('sync');
    setMessage(null);
    try {
      const res = await axios.post(`${API}/provider/calendar/sync`, {}, { headers: authHeaders });
      const c = res.data?.created_count ?? 0;
      const s = res.data?.skipped_count ?? 0;
      setMessage({
        type: 'success',
        text: `Synced ${c} appointment${c === 1 ? '' : 's'} to Google Calendar${s ? ` (${s} skipped)` : ''}.`,
      });
    } catch (e) {
      setMessage({ type: 'error', text: e?.response?.data?.detail || 'Sync failed' });
    } finally {
      setBusy(null);
    }
  };

  const handleDisconnect = async () => {
    setBusy('disconnect');
    setMessage(null);
    try {
      await axios.delete(`${API}/provider/calendar/disconnect`, { headers: authHeaders });
      setConnected(false);
      setConnectedAt(null);
      setMessage({ type: 'success', text: 'Google Calendar disconnected.' });
    } catch (e) {
      setMessage({ type: 'error', text: e?.response?.data?.detail || 'Disconnect failed' });
    } finally {
      setBusy(null);
    }
  };

  const btn = 'inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed';

  return (
    <div className="flex flex-col items-end gap-2" data-testid="calendar-export-toolbar">
      <div className="flex flex-wrap gap-2">
        <button
          onClick={handleDownloadIcs}
          disabled={busy === 'ics'}
          className={`${btn} bg-gray-900 text-white hover:bg-gray-800`}
          data-testid="download-ics-btn"
          title="Download .ics file (works with Apple Calendar, Outlook, Google Calendar)"
        >
          {busy === 'ics' ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
          Download .ics
        </button>

        {!connected ? (
          <button
            onClick={handleConnectGoogle}
            disabled={busy === 'connect'}
            className={`${btn} bg-blue-600 text-white hover:bg-blue-700`}
            data-testid="connect-google-calendar-btn"
          >
            {busy === 'connect' ? <Loader2 size={16} className="animate-spin" /> : <Link2 size={16} />}
            Connect Google Calendar
          </button>
        ) : (
          <>
            <button
              onClick={handleSync}
              disabled={busy === 'sync'}
              className={`${btn} bg-emerald-600 text-white hover:bg-emerald-700`}
              data-testid="sync-google-calendar-btn"
            >
              {busy === 'sync' ? <Loader2 size={16} className="animate-spin" /> : <RefreshCw size={16} />}
              Sync to Google Calendar
            </button>
            <button
              onClick={handleDisconnect}
              disabled={busy === 'disconnect'}
              className={`${btn} bg-gray-100 text-gray-700 hover:bg-gray-200`}
              data-testid="disconnect-google-calendar-btn"
              title={connectedAt ? `Connected ${new Date(connectedAt).toLocaleString()}` : ''}
            >
              {busy === 'disconnect' ? <Loader2 size={16} className="animate-spin" /> : <Unlink size={16} />}
              Disconnect
            </button>
          </>
        )}
      </div>

      {connected && (
        <div className="flex items-center gap-1 text-xs text-emerald-700" data-testid="calendar-connected-indicator">
          <CalendarCheck size={14} />
          Google Calendar connected
        </div>
      )}

      {message && (
        <div
          className={`text-xs px-3 py-1.5 rounded ${
            message.type === 'success'
              ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
              : 'bg-red-50 text-red-700 border border-red-200'
          }`}
          data-testid="calendar-export-message"
        >
          {message.text}
        </div>
      )}
    </div>
  );
};

export default CalendarExportToolbar;
